type RouteQuery = Record<string, string | string[] | undefined>;

/** Next.js route where Contentful serves the homepage and CMS pages. */
const CMS_CATCH_ALL_PATHNAME = '/[lang]/[[...slug]]';

/**
 * Exact pathnames where the section may appear.
 *
 * Exact match, not prefixes: PDPs sit under the same prefixes as their listing
 * pages, so this excludes them — and any future route — by omission.
 */
const ALLOWED_PATHNAMES = new Set([
  '/[lang]/watches/all',
  '/[lang]/watches/[...slug]',
  '/[lang]/collections/[...slug]',
  '/[lang]/straps/all',
  '/[lang]/straps/[material]',
  '/[lang]/straps/packs/[slug]',
  '/[lang]/sunglasses/all',
  '/[lang]/search/[[...urlParts]]',
]);

/** Contentful page type that counts as commerce browsing. */
const ELIGIBLE_CMS_PAGE_TYPE = 'Editorial';

/**
 * Top-level sections the ticket excludes, which Contentful nonetheless types
 * Editorial and serves through the catch-all.
 *
 * Load-bearing, not defensive: a section with no index page file — `/service/`
 * is the live case, with 21 Editorial pages beneath it — never matches a route
 * of its own, so only the slug identifies it. The rest are listed because the
 * same becomes true of any of them the day its page file moves.
 */
const EXCLUDED_CMS_SLUGS = new Set([
  'contact',
  'my-account',
  'news',
  'newsletter',
  'notify-me',
  'service',
  'store',
  'stores',
  'tracking',
  'view-product',
  'watch-comparison',
  'wishlist',
]);

const isHomepage = (query: RouteQuery) =>
  !query.slug || (Array.isArray(query.slug) && query.slug.length === 0);

const isEligibleCmsPage = (query: RouteQuery, pageType?: string | null) => {
  if (pageType !== ELIGIBLE_CMS_PAGE_TYPE) return false;

  const [firstSegment] = Array.isArray(query.slug) ? query.slug : [query.slug];
  return !!firstSegment && !EXCLUDED_CMS_SLUGS.has(firstSegment);
};

/**
 * Whether the section may render on this route.
 *
 * Match on `pathname`, never `asPath`: route precedence has already resolved
 * collisions like `/straps/packs/[slug]` vs `/straps/[material]/[sku]`.
 * `pageType` applies only to the CMS catch-all — Editorial in, Legal out.
 */
export const isRecentlyViewedAllowed = (
  pathname: string,
  query: RouteQuery,
  pageType?: string | null,
): boolean => {
  if (pathname === CMS_CATCH_ALL_PATHNAME) {
    return isHomepage(query) || isEligibleCmsPage(query, pageType);
  }

  return ALLOWED_PATHNAMES.has(pathname);
};
